/** 
  * @typedef {"last" | "this" | "next"} relativePeriod
  * @typedef {"day" | "week" | "month" | "year"} relativeUnit
  * @typedef {"start" | "end"} dateSpec
  * 
  * @typedef {Object} daterange
  * @property {String} from
  * @property {String} to
  * 
  * @typedef {Object} relativeDate
  * @property {relativePeriod} period
  * @property {relativeUnit} unit
  * 
  * @typedef { "isEmpty" | "isNotEmpty" } filteremptyoper
  * 
  * @typedef { "is" | "isNot" | "contains" | "containsNot" 
  *  | "startsWith" | "endsWidth"
  * } filtertextoper
  * 
  * @typedef { "contains" | "containsNot" } filterarrayoper
  * 
  * @typedef { "is" | "isNot" } filterselectoper
  * 
  * @typedef { "is"
  *  | "isBefore" | "isAfter"
  *  | "isOnOrBefore" | "isOnOrAfter"
  *  | "isBetween"
  *  | "isRelativeToToday"
  * } filtertimeoper
  * 
  * @typedef { "equals"
  *  | "notEqual" | "larger" | "smaller" 
  *  | "equalsOrLarger" | "equalsOrSmaller"
  * } filtermathoper
  * 
  * @typedef { filtertextoper | filterarrayoper 
  * | filterselectoper | filtertimeoper | filtermathoper | filteremptyoper } filteroper
  * 
  * @typedef { "OR" | "AND" } filterlogic
  * 
  * @typedef { "text" | "url" | "email" | "phone" | "id"
  *  | "lang"
  *  | "number"
  *  | "user" 
  *  | "time" | "date" 
  *  | "select" | "mutli-select"} filterformat
  * 
  * @typedef {Object} filterRule
  * @property {String} label
  * @property {String | String[]} name
  * @property {filterformat} format
  * @property {filteroper} operator
  * @property {dateSpec} [dateSpec]
  * @property {any} value
  * @property {filterGroup} [group]
  * 
  * @typedef {Object} filterGroup
  * @property {filterRule[]} rules
  * @property {filterlogic} [logic] default: "AND"
  * 
  * @typedef {filterGroup} filterX
  * 
  * @typedef {Object} setrules
  * @property {String} name
  * @property {filteroper} operator
  * @property {*} value
  */

  import _ from "lodash";
  import Accessor from "./Accessor";
  import ZTime from "./ZTime";
  import ZArray from "./ZArray";

  class FilterX {

  static __Oper = {
    "isEmpty": {
      cap: {EN: "is empty", TC: "是空的"},
      val: "isEmpty"
    },
    "isNotEmpty": {
      cap: {EN: "is not empty", TC: "不是空的"},
      val: "isNotEmpty"
    },
    "is": {
      cap: {EN: "is", TC: "是"},
      val: "is"
    },
    "isNot": {
      cap: {EN: "is not", TC: "不是"},
      val: "isNot"
    },
    "contains": {
      cap: {EN: "contains", TC: "包含"},
      symbol: "∋",
      val: "contains"
    },
    "containsNot": {
      cap: {EN: "doesn't contain", TC: "不包含"},
      symbol: "∌",
      val: "containsNot"
    },
    "startsWith": {
      cap: {EN: "starts with", TC: "開始為"},
      val: "startsWith"
    },
    "endsWidth": {
      cap: {EN: "ends with", TC: "結尾為"},
      val: "endsWith"
    },
    "isBefore": {
      cap: {EN: "is before", TC: "早於"},
      symbol: "<",
      val: "isBefore"
    },
    "isAfter": {
      cap: {EN: "is after", TC: "遲於"},
      symbol: ">",
      val: "isAfter"
    },
    "isOnOrBefore": {
      cap: {EN: "is on or before", TC: "於或早於"},
      symbol: "≤",
      val: "isOnOrBefore"
    },
    "isOnOrAfter": {
      cap: {EN: "is on or after", TC: "於或遲於"},
      symbol: "≥",
      val: "isOnOrAfter"
    },
    "isBetween": {
      cap: {EN: "is between", TC: "於...之間"},
      symbol: "<...<",
      val: "isBetween"
    },
    "isRelativeToToday": {
      cap: {EN: "is relative to today", TC: "相對於今天"},
      symbol: "~",
      val: "isRelativeToToday"
    },
    "equals": {
      cap: "=",
      val: "equals"
    },
    "notEqual": {
      cap: "≠",
      val: "notEqual"
    },
    "larger": {
      cap: ">",
      val: "larger"
    },
    "smaller": {
      cap: "<",
      val: "smaller"
    },
    "equalsOrLarger": {
      cap: "≥",
      val: "equalsOrLarger"
    },
    "equalsOrSmaller": {
      cap: "≤",
      val: "equalsOrSmaller"
    },
  }

  static __Period = [
    {
      cap: {EN: "Last", TC: "上"},
      val: "last"
    },
    {
      cap: {EN: "This", TC: "這"},
      val: "this"
    },
    {
      cap: {EN: "Next", TC: "下"},
      val: "next"
    }
  ];

  static __Unit = [
    {
      cap: {EN: "Day", TC: "日"},
      val: "day"
    },
    {
      cap: {EN: "Week", TC: "周"},
      val: "week"
    },
    {
      cap: {EN: "Month", TC: "月"},
      val: "month"
    },
    {
      cap: {EN: "Year", TC: "年"},
      val: "year"
    }
  ]

  /**
    * @template T
    * @param {T[]} docs 
    * @param {filterX} filter
    * @param {String} idAccessor
    * @returns {T[]}
    */
  static Filter(docs, filter, idAccessor = "_id"){
    if(_.isEmpty(filter)) return docs;
    let filterIDs = this.ResolveGroup(docs, filter, idAccessor);
    let filteredDocs = _.filter(docs, o => filterIDs?.includes(Accessor.Get(o, idAccessor)));
    return filteredDocs;
  }

  /**
    * 
    * @param {*[]} docs 
    * @param {filterGroup} group 
    * @param {String} idAccessor
    * @returns {String[]}
    */
  static ResolveGroup(docs, group, idAccessor = "_id"){
    let filteredIDs = null;
    _.map(group.rules, (o, i) => {
      let fIDs = [];
      if(o.group){
        fIDs = this.ResolveGroup(docs, o.group, idAccessor);
      }else{
        let filtered = _.filter(docs, v => this.ResolveRule(v, o));
        fIDs = _.map(filtered, v => Accessor.Get(v, idAccessor));
      }

      if(!filteredIDs){
        filteredIDs = fIDs;
      }else{
        if(group.logic === "OR"){
          filteredIDs = _.union(filteredIDs, fIDs);
        }else{
          filteredIDs = _.intersection(filteredIDs, fIDs);
        }
      }
    });
    return filteredIDs;
  }

  /**
    * 
    * @param {*} doc 
    * @param {filterRule} rule 
    */
  static ResolveRule(doc, rule){
    let {operator, value, dateSpec} = rule;
    let allNames = ZArray.Ensure(rule.name);
    
    let filterResult = _.map(allNames, (o, i) => {
      let fieldValue = Accessor.Get(doc, o);

      if(_.isNull(fieldValue) || _.isUndefined(fieldValue) 
        || (_.isArray(fieldValue) && _.isEmpty(fieldValue))){
          if(rule.operator === "isEmpty") { return true; }
          else { return false; }
      }else{
        if(rule.operator === "isNotEmpty") { return true; }
        if(rule.operator === "isEmpty") { return false; }
        if(!value) return true;
        try{
          switch(rule.format){
            case "text": case "url": case "email": case "phone": case "id":
              return this.ResolveTextRule(fieldValue, operator, value, rule);
            case "lang":
              return this.ResolveLangRule(fieldValue, operator, value, rule);
            case "number": 
              return this.ResolveMathRule(fieldValue, operator, value);
            case "date":
              let {from, to} = fieldValue;
              let cmpValue = dateSpec === "end" ? to : from;
              return this.ResolveTimeRule(cmpValue, operator, value);
            case "time":
              return this.ResolveTimeRule(fieldValue, operator, value);
            case "select":
              return this.ResolveSelectRule(fieldValue, operator, value);
            case "mutli-select": case "user":
              return this.ResolveMultiSelectRule(fieldValue, operator, value);
            default: return false;
          }
        }catch(e){
          console.error(e);
          return false;
        }
      }
    });
    if(rule.operator === "isEmpty"){
      return _.every(filterResult, o => o === true);
    }
    return _.some(filterResult, o => o === true);
  }

  /**
    * 
    * @param {String} fieldValue 
    * @param {filterselectoper} oper 
    * @param {String | String[]} value 
    */
  static ResolveSelectRule(fieldValue, oper, value = null){
    value = ZArray.Ensure(value);
    switch(oper){
      case "is": return value.includes(fieldValue);
      case "isNot": return !value.includes(fieldValue);
      default: return false;
    }
  }

  /**
    * 
    * @param {String[]} fieldValue 
    * @param {filterarrayoper} oper 
    * @param {String | String[]} value 
    */
  static ResolveMultiSelectRule(fieldValue, oper, value = null){
    value = ZArray.Ensure(value);
    switch(oper){
      case "contains": return ZArray.IncludesEither(fieldValue, value);
      case "containsNot": return ZArray.MissingAll(fieldValue, value);
      default: return false;
    }
  }

  /**
    * 
    * @param {String} fieldValue 
    * @param {filtertimeoper} oper 
    * @param {String | daterange} value 
    */
  static ResolveTimeRule(fieldValue, oper, value = null){
    let cmpValue = fieldValue;
    switch(oper){
      default: case "is":
        if(_.isString(value)) return ZTime.IsSame(cmpValue, value);
        return ZTime.IsBetween(cmpValue, value.from, value.to, "[]");
      case "isAfter": return ZTime.IsAfter(cmpValue, value);
      case "isBefore": return ZTime.IsBefore(cmpValue, value);
      case "isBetween": return ZTime.IsBetween(cmpValue, value.from, value.to, "[]");
      case "isOnOrAfter": return ZTime.IsSameOrAfter(cmpValue, value);
      case "isOnOrBefore": return ZTime.IsSameOrBefore(cmpValue, value);
      case "isRelativeToToday": return this.ResolveRelativeTime(cmpValue, value);
    }
  }

  /**
    * 
    * @param {String} fieldValue 
    * @param {relativeDate} value 
    */
  static ResolveRelativeTime(fieldValue, value){
    let {period, unit} = value;
    let now = ZTime.Now();
    let refDate = now;
    switch(period){
      case "last": refDate = ZTime.Add(refDate, -1, unit).toISOString(); break;
      default: case "this": break;
      case "next": refDate = ZTime.Add(refDate, 1, unit).toISOString(); break;
    }
    let startDate = null;
    let endDate = null;
    switch(unit){
      default: case "day": 
        startDate = ZTime.StartOfDay(refDate).toISOString();
        endDate = ZTime.EndOfDay(refDate).toISOString();
        break;
      case "week":
        startDate = ZTime.StartOfWeek(refDate).toISOString();
        endDate = ZTime.EndOfWeek(refDate).toISOString();
        break;
      case "month":
        startDate = ZTime.StartOfMonth(refDate).toISOString();
        endDate = ZTime.EndOfMonth(refDate).toISOString();
        break;
      case "year":
        startDate = ZTime.StartOfYear(refDate).toISOString();
        endDate = ZTime.EndOfYear(refDate).toISOString();
        break;
    }
    return ZTime.IsBetween(fieldValue, startDate, endDate, "[]");
  }

  /**
    * 
    * @param {Number} fieldValue 
    * @param {filtermathoper} oper 
    * @param {Number} value 
    */
  static ResolveMathRule(fieldValue, oper, value = null){
    let cmpNum = Number(fieldValue);
    let cmpVal = Number(value);
    switch(oper){
      case "equals": return cmpNum === cmpVal;
      case "notEqual": return cmpNum !== cmpVal;
      case "equalsOrLarger": return cmpNum >= cmpVal;
      case "equalsOrSmaller": return cmpNum <= cmpVal;
      case "larger": return cmpNum > cmpVal;
      case "smaller": return cmpNum < cmpVal;
      default: return false;
    }
  }

  /**
  * 
  * @param {String} fieldValue 
  * @param {filtertextoper} oper
  * @param {String} value 
  * @param {filterRule} rule
  */
  static ResolveLangRule(fieldValue, oper, value = null, rule = null){
    let allValues = [];
    if(_.isString(fieldValue)){
      allValues = [fieldValue];
    }else if(_.isArray(fieldValue)){
      _.map(fieldValue, o => {
        if(_.isString(o)){
          allValues.push(o);
        }else{
          let kvps = Accessor.NestedToKeyValuePairs(o);
          _.map(kvps, v => allValues.push(v));
        }
      });
    }else{
      let kvps = Accessor.NestedToKeyValuePairs(fieldValue);
      allValues = _.map(kvps, o => o);
    } 
    let cmpValue = allValues.join(" ");
    return this.ResolveTextRule(cmpValue, oper, value, rule);
  }

  /**
  * 
  * @param {String | String[]} fieldValue 
  * @param {filtertextoper} oper
  * @param {String} value 
  * @param {filterRule} rule
  */
  static ResolveTextRule(fieldValue, oper, value = "", rule){

    if(_.isEmpty(value)) return true;
    
    let { separator, ignore } = rule;
    let _fieldValue = fieldValue;
    if(_.isArray(_fieldValue)){
      _fieldValue = fieldValue.join(" ");
    }
    _fieldValue = _fieldValue.toLowerCase();
    let _value = value.toLowerCase();

    _value = !_.isEmpty(ignore) ? _value.replace(ignore, " ") : _value;
    _value = !_.isEmpty(separator) ? _value.split(separator) : _value;
    _fieldValue = !_.isEmpty(ignore) ? _fieldValue.replace(ignore, " ") : _fieldValue;

    switch(oper){
      case "contains": return ZArray.IncludesEither(_fieldValue, _value);
      case "containsNot": return !ZArray.IncludesEither(_fieldValue, _value);
      case "endsWidth": return _fieldValue.endsWith(_value);
      case "startsWith": return _fieldValue.startsWith(_value);
      case "is": return _fieldValue === _value;
      case "isNot": return _fieldValue !== _value;
      case "isEmpty": return _.isEmpty(_fieldValue);
      case "isNotEmpty": return !_.isEmpty(_fieldValue);
      default: return false;
    }
  }

  /**
    * 
    * @param {filterX} filter 
    */
  static toMongoQuery(filter){
    
  }

  /**
    * 
    * @param {filteroper[]} rules 
    * @returns 
    */
  static GetRef(rules){
    let rtn = [];
    _.map(rules, o => {
      if(this.__Oper[o]){
        rtn.push(this.__Oper[o]);
      }
    });
    return rtn;
  }

  /**
    * 
    * @param {filterformat} format 
    * @param {filteroper} operator 
    * @returns {"none" | "number" | "text" | "select" | "date" | "dateRange" | "relativeDate"}
    */
  static GetValueType(format, operator){
    if(operator === "isEmpty" || operator === "isNotEmpty"){
      return "none";
    }

    switch(format){
      default: case "text": case "url": case "email": 
      case "phone": case "id": case "lang":
        return "text";
      case "number":
        return "number";
      case "time": case "date":
        if(operator === "isBetween") return "dateRange";
        if(operator === "isRelativeToToday") return "relativeDate";
        return "date";
      case "select": 
      case "mutli-select": case "user":
        return "select";
    }
  }

  /**
    * 
    * @param {filterformat} format 
    */
  static GetSelectRef(format){
    switch(format){
      default: case "text": case "url": case "email": 
      case "phone": case "id": case "lang":
        return this.GetRef([ 
          "is", "isNot", 
          "contains", "containsNot",
          "startsWith", "endsWith",
          "isEmpty", "isNotEmpty"
        ]);
      case "number":
        return this.GetRef([ 
          "equals", "notEqual", 
          "smaller", "larger",
          "equalsOrSmaller", "equalsOrLarger",
          "isEmpty", "isNotEmpty"
        ]);
      case "time": case "date":
        return this.GetRef([ 
          "is", "isBefore", "isAfter",
          "isOnOrBefore", "isOnOrAfter",
          "isBetween", "isRelativeToToday",
          "isEmpty", "isNotEmpty"
        ]);
      case "select": 
        return this.GetRef([ 
          "is", "isNot",
          "isEmpty", "isNotEmpty"
        ]);
      case "mutli-select": case "user":
        return this.GetRef([ 
          "contains", "containsNot",
          "isEmpty", "isNotEmpty"
        ]);
    }
  }

  /**
    * 
    * @param {filterformat} format 
    * @returns {"equals" | "contains" | "is" | "isRelativeToToday"}
    */
  static GetDefaultOper(format){
    switch(format){
      case "number": 
        return "equals";
      default: 
      case "text":
      case "url":
      case "email":
      case "phone":
      case "id":
      case "lang":
      case "mutli-select":
        return "contains";
      case "select":
      case "user":
        return "is";
      case "time":
      case "date":
        return "isRelativeToToday";
    }
  }

  /**
  * 
  * @param {import("../LabIZO/Filterizo/__typedef").filterSchema} schema 
  * @param {setrules[]} rules
  */
  static NewFilter(schema, rules){
    rules = ZArray.Ensure(rules);

    /**
    * @type {filterX}
    */
    let rtn = {
      rules: [],
      logic: "AND"
    };

    _.map(rules, o => {
      let ischema = _.find(schema, v => {
        if(_.isString(v.name)){
          return v.name === o.name;
        }else if(_.isArray(v.name)){
          return ZArray.IncludesAll(v.name, o.name);
        }
      });
      if(ischema){
        rtn.rules.push({
          ...ischema,
          operator: o.operator,
          value: o.value
        });
      }
    });
    return rtn;
  }

  /**
   * 
   * @param {*} doc 
   * @param {String[]} fields 
   * @param {String} search 
   */
  static TextSearchForFields(doc, fields, search){
    fields = ZArray.Ensure(fields);
    let _caps = [];
    _.map(fields, (o, i) => {
      let value = Accessor.Get(doc, o);
      if(!_.isNil(value)){
        if(_.isNumber(value)) value = String(value);
        if(_.isString(value)){
          _caps.push(value?.toLowerCase?.());
        }
      }
    });
    let _cap = _caps.join(" ");
    let _search = search?.toLowerCase?.();
    return _cap.includes(_search);
  }

}

export default FilterX;